// client/src/pages/Profile.jsx

import React, { useState, useEffect } from 'react';

import { toast } from 'react-toastify';

import { Link } from 'react-router-dom';

import api from '../utils/api';

import { useAuth } from '../contexts/AuthContext';

import { useOrg } from '../contexts/OrgContext';

import { supabase } from '../utils/supabaseClient';

import ManageBillingButton from '../components/billing/ManageBillingButton';

import PlanManager from '../components/billing/PlanManager';

const USERNAME_REGEX = /^[a-z0-9_.]+$/;

export default function Profile() {

  const { user, signOut } = useAuth();

  const { organization, permissions, refresh, profile } = useOrg();

  const [form, setForm] = useState({ name: '', email: '', username: '', password: '' });

  const [saving, setSaving] = useState(false);



  // subscription state for the new card

  const [sub, setSub] = useState(null);

  const [loadingSub, setLoadingSub] = useState(true);



  // Re-mount invite/list when org membership changes



  useEffect(() => {

    if (!user) return;
    const profileName =
      profile?.full_name ||
      user.user_metadata?.name ||
      user.name ||
      '';
    const profileUsername =
      profile?.username ||
      user.user_metadata?.username ||
      '';

    setForm((prev) => ({
      ...prev,
      name: prev.name || profileName,
      email: user.email || '',
      username: prev.username || profileUsername,
      password: '',
    }));

  }, [user, profile]);



  // Load current user's subscription (plan/status/renewal)

  useEffect(() => {

    let active = true;

    (async () => {

      setLoadingSub(true);

      try {

        const { data: u } = await supabase.auth.getUser();

        const userId = u.user?.id;

        if (!userId) { if (active) setSub(null); return; }



        const { data, error } = await supabase

          .from('subscriptions')

          .select('plan_id, status, current_period_end')

          .eq('user_id', userId)

          .maybeSingle();



        if (error) console.warn('subscriptions read error:', error.message);

        if (active) setSub(data ?? null);

      } finally {

        if (active) setLoadingSub(false);

      }

    })();

    return () => { active = false; };

  }, []);



  const handleSubmit = async (e) => {

    e.preventDefault();

    const normalizedUsername = (form.username || '').trim().toLowerCase();
    if (!normalizedUsername) {
      toast.error('Please add a username to continue.');
      return;
    }
    if (normalizedUsername.length < 3 || normalizedUsername.length > 32) {
      toast.error('Username must be between 3 and 32 characters.');
      return;
    }
    if (!USERNAME_REGEX.test(normalizedUsername)) {
      toast.error('Use letters, numbers, underscores, or dots only.');
      return;
    }
    if (form.password && form.password.length > 0 && form.password.length < 6) {
      toast.error('Passwords must be at least 6 characters long.');
      return;
    }

    setSaving(true);

    try {

      await api.put('/auth/profile', { ...form, username: normalizedUsername });



      const { error: metaErr } = await supabase.auth.updateUser({

        data: { name: form.name || '', username: normalizedUsername },

      });

      if (metaErr) {

        console.error('Supabase metadata update failed:', metaErr.message);

        toast.warn('Saved, but could not update display name in auth. Try again later.');

      } else {

        toast.success('Profile updated successfully!');

      }


      await refresh();

      setForm((f) => ({ ...f, username: normalizedUsername, password: '' }));

    } catch (err) {

      console.error('Profile update error:', err);

      const apiMessage = err?.response?.data?.error || err?.message || 'Error updating profile. Try again!';

      toast.error(apiMessage);

    } finally {

      setSaving(false);

    }

  };



  // --- Design tokens ---

  const card =

    'glass-card';

  const input =

    'glass-input w-full';

  const btnPrimary =

    'glass-cta text-sm disabled:opacity-60 disabled:cursor-not-allowed';

  const btnGhost =

    'glass-ghost text-sm';

  const usernameMissing = !form.username?.trim();



  const initials =

    (form.name?.trim() || form.username || form.email || '?')

      .split(' ')

      .map((p) => p[0])

      .join('')

      .slice(0, 2)

      .toUpperCase();



  const fmtDate = (iso) => {

    if (!iso) return '--';

    const d = new Date(iso);

    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

  };



  return (

    <div className="mx-auto max-w-5xl px-6 py-10 text-zinc-200">

      {/* Page header */}

      <div className="mb-8 flex items-center justify-between">

        <h2 className="text-3xl font-semibold tracking-tight">

          <span className="bg-gradient-to-r from-amber-200 to-amber-500 bg-clip-text text-transparent">

            Your Profile

          </span>

        </h2>

        <button onClick={signOut} className={btnGhost}>Logout</button>

      </div>



      {/* 2-col layout on large screens */}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

        {/* Profile card */}

        <div className={`${card} p-6`}>

          <div className="mb-6 flex items-center gap-4">

            <div className="grid h-14 w-14 place-items-center rounded-full border border-white/10 bg-gradient-to-br from-amber-400/20 via-white/5 to-teal-300/15 shadow-[0_12px_30px_-16px_rgba(246,196,82,0.8)]">

              <span className="text-sm font-semibold text-zinc-200">{initials}</span>

            </div>

            <div className="leading-tight">

              <div className="text-lg font-semibold">{form.name || '--'}</div>

              <div className="text-sm text-zinc-400">{form.email || '--'}</div>

            </div>

          </div>


          {usernameMissing && (
            <div className="mb-4 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              Add a username to finish setting up your account.
            </div>
          )}


          <form onSubmit={handleSubmit} className="space-y-4">

            <div>

              <label htmlFor="name" className="mb-1 block text-sm text-zinc-400">Name</label>

              <input

                id="name"

                className={input}

                placeholder="Name"

                value={form.name}

                onChange={(e) => setForm({ ...form, name: e.target.value })}

              />

            </div>



            <div>

              <label htmlFor="username" className="mb-1 block text-sm text-zinc-400">Username</label>

              <input

                id="username"

                className={input}

                placeholder="yourname"

                value={form.username}

                autoComplete="username"

                onChange={(e) => setForm({ ...form, username: (e.target.value || '').toLowerCase() })}

              />

              <p className="mt-1 text-xs text-zinc-500">Required. 3-32 characters, letters/numbers/._ only.</p>

            </div>


            <div>

              <label htmlFor="email" className="mb-1 block text-sm text-zinc-400">Email</label>

              <input

                id="email"

                disabled

                className={`${input} disabled:opacity-60`}

                value={form.email}

                readOnly

              />

              <p className="mt-1 text-xs text-zinc-500">Email is managed by your account.</p>

            </div>



            <div>

              <label htmlFor="password" className="mb-1 block text-sm text-zinc-400">

                New password <span className="text-zinc-500">(optional)</span>

              </label>

              <input

                id="password"

                type="password"

                className={input}

                placeholder="********"

                value={form.password}

                onChange={(e) => setForm({ ...form, password: e.target.value })}

              />

            </div>



            <button type="submit" className={`${btnPrimary} w-full`} disabled={saving}>

              {saving ? 'Saving...' : 'Save changes'}

            </button>

          </form>

        </div>



        {/* Workspace summary */}

        <div className={`${card} p-6`}>

          <div className="mb-4">

            <h3 className="text-xl font-semibold">

              <span className="bg-gradient-to-r from-amber-200 to-amber-500 bg-clip-text text-transparent">

                Workspace

              </span>

            </h3>

            <p className="mt-1 text-sm text-zinc-400">

              {organization ? organization.name : 'Your workspace will be created automatically when you start collaborating.'}

            </p>

          </div>



          {organization ? (

            <dl className="grid grid-cols-1 gap-y-2 text-sm text-zinc-300">

              <div>

                <dt className="text-zinc-500">Plan</dt>

                <dd className="font-semibold">{organization.plan?.toUpperCase()}</dd>

              </div>

              <div>

                <dt className="text-zinc-500">Status</dt>

                <dd className="font-semibold capitalize">{organization.plan_status}</dd>

              </div>

            </dl>

          ) : (

            <p className="text-sm text-zinc-500">Create your first project to spin up a workspace.</p>

          )}



          {(permissions.canAccessAdmin || permissions.role === 'admin') && (

            <div className="mt-4">

              <Link to="/admin" className={btnPrimary}>

                Open Admin Console

              </Link>

            </div>

          )}

        </div>



        {/* Subscription & Billing */}

        <div className="space-y-4 lg:col-span-2">

          <PlanManager onPlanChange={refresh} />



          <div className={`${card} p-6`}>

            <div className="mb-4 flex items-center justify-between">

              <h3 className="text-xl font-semibold">

                <span className="bg-gradient-to-r from-amber-200 to-amber-500 bg-clip-text text-transparent">

                  Billing Portal & Invoices

                </span>

              </h3>

            </div>



            <div className="rounded-xl border border-white/10 bg-zinc-900/50 p-4">

              {loadingSub ? (

                <p className="text-sm text-zinc-400">Loading subscription...</p>

              ) : (

                <>

                  <dl className="grid grid-cols-1 gap-y-1 text-sm sm:grid-cols-3">

                    <div className="flex justify-between sm:block">

                      <dt className="text-zinc-400">Plan ID</dt>

                      <dd className="text-zinc-200">{sub?.plan_id ?? '--'}</dd>

                    </div>

                    <div className="flex justify-between sm:block">

                      <dt className="text-zinc-400">Status</dt>

                      <dd className="text-zinc-200 capitalize">{sub?.status ?? '--'}</dd>

                    </div>

                    <div className="flex justify-between sm:block">

                      <dt className="text-zinc-400">Renews</dt>

                      <dd className="text-zinc-200">{fmtDate(sub?.current_period_end)}</dd>

                    </div>

                  </dl>



                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">

                    <ManageBillingButton className={`${btnPrimary} w-full sm:w-auto`} />

                    <p className="text-xs text-zinc-500 sm:ml-3">

                      Opens the Stripe customer portal for invoices and payment methods.

                    </p>

                  </div>

                </>

              )}

            </div>

          </div>

        </div>



        <div className={`${card} p-6 lg:col-span-2`}>

          <div className="mb-3">

            <h3 className="text-xl font-semibold">

              <span className="bg-gradient-to-r from-amber-200 to-amber-500 bg-clip-text text-transparent">

                Collaboration

              </span>

            </h3>

            <p className="text-sm text-zinc-400">

              Invite teammates and allocate seats when your workspace is on the Enterprise plan.

            </p>

          </div>



          {organization?.plan === 'enterprise' ? (

            <div className="space-y-3 text-sm text-zinc-300">

              <p>

                Use the Admin Console to invite teammates, assign roles, and manage seat allocations for your company.

              </p>

              {permissions.canAccessAdmin || permissions.role === 'admin' ? (

                <Link to="/admin" className={`${btnPrimary} inline-flex`}>

                  Manage Team

                </Link>

              ) : (

                <p className="text-xs text-zinc-500">

                  Contact a workspace admin if you need collaboration access.

                </p>

              )}

            </div>

          ) : (

            <p className="text-sm text-zinc-500">

              Collaboration tools are limited to Enterprise workspaces. Upgrade when you need shared access and seat

              management.

            </p>

          )}

        </div>

      </div>

    </div>

  );

}
